import { useTranslation } from 'react-i18next';
import type { Account, Item, TaxRate, Warehouse } from '../../types/bookkeeping.js';

// percentage is stored × 1,000,000 (so 15% = 15_000_000n).
// Divisor below = 100 (percent → fraction) × 1_000_000 (scale) = 100_000_000.
const TAX_DIVISOR = 100_000_000n;

export interface InvoiceLineFormValue {
  item_id: string;
  description: string;
  quantity: string; // bigint × 1000 as string
  unit_price: string; // bigint minor units as string
  tax_rate_id: string;
  account_id: string;
  warehouse_id: string; // required when item.type === 'inventory'
}

export interface InvoiceLineRowProps {
  index: number;
  value: InvoiceLineFormValue;
  items: Item[];
  taxRates: TaxRate[];
  revenueAccounts: Account[];
  warehouses: Warehouse[];
  canRemove: boolean;
  onChange: (v: InvoiceLineFormValue) => void;
  onRemove: () => void;
}

function computeLineAmounts(
  quantity: string,
  unitPrice: string,
  taxRatePercentage: string,
): { subtotal: string; taxAmount: string; total: string } {
  try {
    const qty = BigInt(quantity);
    const price = BigInt(unitPrice);
    const pct = BigInt(taxRatePercentage);

    // subtotal = qty × price / 1000 (quantity is × 1000)
    const subtotal = (qty * price) / 1000n;
    // taxAmount = subtotal × percentage / 100_000_000 (percent×scale)
    const taxAmount = (subtotal * pct) / TAX_DIVISOR;
    const total = subtotal + taxAmount;

    return {
      subtotal: subtotal.toString(),
      taxAmount: taxAmount.toString(),
      total: total.toString(),
    };
  } catch {
    return { subtotal: '—', taxAmount: '—', total: '—' };
  }
}

export function InvoiceLineRow({
  value,
  items,
  taxRates,
  revenueAccounts,
  warehouses,
  canRemove,
  onChange,
  onRemove,
}: InvoiceLineRowProps) {
  const { t } = useTranslation('bookkeeping');

  const selectedItem = items.find((i) => i.id === value.item_id);
  const isInventory = selectedItem?.type === 'inventory';

  const selectedTaxRate = taxRates.find((r) => r.id === value.tax_rate_id);

  // When item is selected, auto-fill description, unit price, and account
  function handleItemChange(itemId: string) {
    const item = items.find((i) => i.id === itemId);
    if (item) {
      onChange({
        ...value,
        item_id: itemId,
        description: item.description ?? value.description,
        unit_price:
          item.default_price_amount != null ? item.default_price_amount : value.unit_price,
        account_id: item.default_revenue_account_id ?? value.account_id,
        // clear warehouse when switching to non-inventory
        warehouse_id: item.type === 'inventory' ? value.warehouse_id : '',
      });
    } else {
      onChange({ ...value, item_id: itemId, warehouse_id: '' });
    }
  }

  const taxPercentage = selectedTaxRate?.percentage ?? '0';
  const computed =
    value.quantity && value.unit_price
      ? computeLineAmounts(value.quantity, value.unit_price, taxPercentage)
      : null;

  return (
    <tr className="border-b">
      {/* Item */}
      <td className="px-2 py-1">
        <select
          value={value.item_id}
          onChange={(e) => handleItemChange(e.target.value)}
          className="w-full min-w-28 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">—</option>
          {items.map((it) => (
            <option key={it.id} value={it.id}>
              {it.name}
            </option>
          ))}
        </select>
      </td>
      {/* Description */}
      <td className="px-2 py-1">
        <input
          value={value.description}
          onChange={(e) => onChange({ ...value, description: e.target.value })}
          placeholder={t('sales.line.description')}
          className="w-40 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      {/* Quantity */}
      <td className="px-2 py-1">
        <input
          value={value.quantity}
          onChange={(e) => onChange({ ...value, quantity: e.target.value })}
          placeholder="1000"
          className="w-20 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      {/* Unit price */}
      <td className="px-2 py-1">
        <input
          value={value.unit_price}
          onChange={(e) => onChange({ ...value, unit_price: e.target.value })}
          placeholder="0"
          className="w-24 rounded border px-1 py-0.5 text-sm"
        />
      </td>
      {/* Tax rate */}
      <td className="px-2 py-1">
        <select
          value={value.tax_rate_id}
          onChange={(e) => onChange({ ...value, tax_rate_id: e.target.value })}
          className="w-28 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">— {t('sales.line.tax_rate')}</option>
          {taxRates.map((r) => (
            <option key={r.id} value={r.id}>
              {r.name} ({r.code})
            </option>
          ))}
        </select>
      </td>
      {/* Account */}
      <td className="px-2 py-1">
        <select
          value={value.account_id}
          onChange={(e) => onChange({ ...value, account_id: e.target.value })}
          className="w-full min-w-32 rounded border px-1 py-0.5 text-sm"
        >
          <option value="">— {t('sales.line.account')}</option>
          {revenueAccounts.map((a) => (
            <option key={a.id} value={a.id}>
              {a.code} {a.name}
            </option>
          ))}
        </select>
      </td>
      {/* Warehouse — only shown for inventory items */}
      <td className="px-2 py-1">
        {isInventory && (
          <select
            value={value.warehouse_id}
            onChange={(e) => onChange({ ...value, warehouse_id: e.target.value })}
            className="w-full min-w-32 rounded border px-1 py-0.5 text-sm"
            required
          >
            <option value="">— {t('inventory.line.warehouse')}</option>
            {warehouses
              .filter((w) => w.is_active)
              .map((w) => (
                <option key={w.id} value={w.id}>
                  {w.name}
                </option>
              ))}
          </select>
        )}
      </td>
      {/* Subtotal */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-600">
        {computed?.subtotal ?? '—'}
      </td>
      {/* Tax */}
      <td className="px-2 py-1 text-right font-mono text-xs text-slate-600">
        {computed?.taxAmount ?? '—'}
      </td>
      {/* Total */}
      <td className="px-2 py-1 text-right font-mono text-xs font-medium">
        {computed?.total ?? '—'}
      </td>
      {/* Remove */}
      <td className="px-2 py-1">
        <button
          type="button"
          onClick={onRemove}
          disabled={!canRemove}
          className="text-red-600 hover:text-red-700 disabled:cursor-not-allowed disabled:opacity-30"
          title="Eliminar línea"
        >
          ×
        </button>
      </td>
    </tr>
  );
}
